import { Button, CopyIcon, confirmDialog, Menu, MenuItem, PencilIcon, ProgressSpinner } from '@repo/atomic-components';
import {
  selectCaseIdData,
  useAppSelector,
  selectCaseStatus,
  selectIsEndpointLockActive,
  selectUserAboutData,
} from '@/store';
import { EndpointLock, useLazyPostCaseCloseQuery } from '@/api';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { AccessControlAction, AppRoutes, CLOSE_CASE_CONFIRM, FLOATING_PANEL_SHADOW } from '@/constants';
import { FC, useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import { classNames } from 'primereact/utils';
import { StartAIProcessingButton } from './StartAIProcessingButton';
import { useAccessControl } from '@/hooks';
import { CaseStatus } from '../utils';
import { useCaseStatusPolling } from '../hooks';
import { useDragAndDropFilesUpload } from '../UploadedMedicalRecords/hooks';
import { UploadFilesButton } from '../UploadedMedicalRecords/components';

interface ActionsPanelProps {
  isDataLoading: boolean;
  handleOpenCaseSidebarClick: () => void;
  handleOpenCloneCaseSidebarClick: () => void;
}

export const ActionsPanel: FC<ActionsPanelProps> = ({
  isDataLoading,
  handleOpenCaseSidebarClick,
  handleOpenCloneCaseSidebarClick,
}) => {
  const caseId = useAppSelector(selectCaseIdData);
  const caseStatus = useAppSelector(selectCaseStatus);
  const userAboutData = useAppSelector(selectUserAboutData);
  const isCreateEditCase = useAccessControl(AccessControlAction.CREATE_EDIT_CASE);
  const isUploadDeleteRawMedicalRecords = useAccessControl(AccessControlAction.UPLOAD_DELETE_RAW_MEDICAL_RECORDS);

  // Upload eligibility — combines the access-control gate with the
  // BE-driven `canUploadDocuments` flag, which flips false while the
  // case is being cloned, while AI is processing, after the case has
  // been closed, etc. The clone lock is checked as well, so a clone
  // keeps uploads off even if a status read disagrees with itself.
  // Drives the Upload files button; CaseItemLayout gates the page-wide
  // drop-zone the same way, except that during a clone it still catches
  // drops and refuses them with the lock's toast.
  const isCaseCloning = useAppSelector((state) => selectIsEndpointLockActive(state, EndpointLock.CASE_CLONING));
  const canUpload = isUploadDeleteRawMedicalRecords && !!caseStatus?.canUploadDocuments && !isCaseCloning;
  const { onFilesStartUpload } = useDragAndDropFilesUpload();

  // Only poll overview and documents when status is CLONING
  const shouldPollOverviewAndDocuments = caseStatus?.status === CaseStatus.CLONING;
  const { isPolling, startPolling, stopPolling } = useCaseStatusPolling(caseId, shouldPollOverviewAndDocuments);

  const [triggerPostCaseClose, { isLoading: isCloseLoading }] = useLazyPostCaseCloseQuery();
  //Polling Logic
  useEffect(() => {
    const statusToStopPolling =
      caseStatus?.status === CaseStatus.READY_FOR_REVIEW ||
      caseStatus?.status === CaseStatus.CLOSED ||
      caseStatus?.status === CaseStatus.CLONING_FAILED ||
      caseStatus?.status === CaseStatus.AI_PAGINATION_CANCELLED ||
      caseStatus?.status === CaseStatus.AI_PAGINATION_FAILED ||
      caseStatus?.status === CaseStatus.AI_TIMELINE_CANCELLED;
    const statusesToAutoStartPolling =
      caseStatus?.status === CaseStatus.CLONING ||
      caseStatus?.status === CaseStatus.DOCUMENT_UPLOAD ||
      caseStatus?.status === CaseStatus.WAITING_FOR_DOCUMENT_PROCESSING ||
      caseStatus?.status === CaseStatus.AI_PAGINATION_PROCESSING ||
      caseStatus?.status === CaseStatus.AI_PAGINATION_COMPLETE ||
      caseStatus?.status === CaseStatus.AI_TIMELINE_PROCESSING ||
      caseStatus?.status === CaseStatus.AI_TIMELINE_COMPLETE;

    if (statusToStopPolling && isPolling) {
      stopPolling();
    } else if (statusesToAutoStartPolling && !isPolling) {
      startPolling();
    }
  }, [caseStatus?.status, isPolling, stopPolling, startPolling]);

  // Determine when to show Start AI Processing button
  const canShowStartButton =
    caseStatus?.status === CaseStatus.NEW ||
    caseStatus?.status === CaseStatus.DOCUMENT_UPLOAD ||
    caseStatus?.status === CaseStatus.WAITING_FOR_DOCUMENT_PROCESSING ||
    caseStatus?.status === CaseStatus.AI_PAGINATION_CANCELLED ||
    caseStatus?.status === CaseStatus.AI_TIMELINE_CANCELLED ||
    caseStatus?.status === CaseStatus.AI_PAGINATION_FAILED ||
    caseStatus?.status === CaseStatus.AI_TIMELINE_FAILED ||
    caseStatus?.status === CaseStatus.READY_FOR_REVIEW ||
    caseStatus?.status === CaseStatus.READY_FOR_AI_PROCESSING;

  //Determine when to show Close Button.
  // const canShowCloseButton = caseStatus?.canClose && caseStatus?.status !== CaseStatus.CLOSED;
  const canShowCloseButton = caseStatus?.status !== CaseStatus.CLOSED;
  const handleEditCaseClick = () => {
    if (handleOpenCaseSidebarClick) {
      handleOpenCaseSidebarClick();
    }
  };

  const handleCloneCaseClick = () => {
    if (handleOpenCloneCaseSidebarClick) {
      handleOpenCloneCaseSidebarClick();
    }
  };

  const closeCaseMenu = useRef<Menu>(null);

  const handleCloseCaseClick = async (notifyProclaim: boolean) => {
    if (caseId) {
      const confirmed = await confirmDialog({
        id: CLOSE_CASE_CONFIRM,
        caseId: caseId,
      });

      if (confirmed) {
        triggerPostCaseClose({
          caseId: caseId,
          notify_proclaim: notifyProclaim,
        });
      }
    }
  };

  const closeCaseMenuItems: MenuItem[] = [
    {
      label: 'Close and export to Proclaim',
      command: () => handleCloseCaseClick(true),
    },
    {
      label: 'Close (no export)',
      command: () => handleCloseCaseClick(false),
    },
  ];

  const handleShowCloseCaseMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (closeCaseMenu.current) {
      closeCaseMenu.current.toggle(event);
    }
  };

  return (
    <div className="border-fletchers-gray-110 bg-fletchers-white flex h-full w-full flex-row items-center gap-4 border-b px-6">
      {isDataLoading ? (
        <div className="flex h-full w-full items-center justify-center gap-4 px-6">
          <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" animationDuration="1.5s" />
        </div>
      ) : (
        <>
          {/* Page-level tabs — fill the full 48px height of the bar.
                Active tab gets a flat green-600 underline; inactive
                tabs read as muted black-900 with no underline. The
                underline uses an inset `box-shadow` rather than
                `border-b` so it sits flush against the bar's own
                bottom border without a 1px double-line gap. */}
          <nav className="flex h-full items-stretch">
            {(
              [
                { to: AppRoutes.UploadedMedicalRecords, label: 'Raw Medical Records' },
                { to: AppRoutes.AiProcessedDocuments, label: 'AI Outputs' },
              ] as const
            ).map((tab) => (
              <NavLink
                key={tab.to}
                to={`/${AppRoutes.CaseList}/${caseId}/${tab.to}`}
                className={({ isActive }) =>
                  classNames(
                    'flex items-center px-3 text-[14px]/[20px] transition-colors',
                    isActive
                      ? 'text-fletchers-green-600 font-[600]'
                      : 'text-fletchers-gray-200 hover:text-fletchers-green-600 font-[500]',
                  )
                }
                style={({ isActive }) =>
                  isActive ? { boxShadow: 'inset 0 -2px 0 var(--color-fletchers-green-600)' } : undefined
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center justify-end gap-2">
            {/* Show Start AI Processing button */}
            {canShowStartButton && <StartAIProcessingButton />}

            {/* Show Upload Files button — gated on the same
                  eligibility flag the page-wide drop-zone uses. */}
            {canUpload && <UploadFilesButton onFilesPicked={onFilesStartUpload} />}

            {/* Show Edit Case button */}
            {isCreateEditCase && (
              <>
                <OutlineButton className="!h-8 !bg-white whitespace-nowrap" onClick={handleEditCaseClick}>
                  <PencilIcon className="h-3 w-3" />
                  <span className="ml-2">Edit</span>
                </OutlineButton>
                <OutlineButton className="!h-8 !bg-white whitespace-nowrap" onClick={handleCloneCaseClick}>
                  <CopyIcon className="h-3 w-3" />
                  <span className="ml-2">Clone</span>
                </OutlineButton>
              </>
            )}

            {/* Show Close Case button */}
            {canShowCloseButton &&
              (userAboutData?.isMls ? (
                <>
                  <Button
                    disabled={isCloseLoading}
                    icon="pi pi-angle-down"
                    iconPos="right"
                    severity="danger"
                    label={isCloseLoading ? 'Closing...' : 'Close'}
                    className="!h-8 px-4 whitespace-nowrap"
                    onClick={handleShowCloseCaseMenuClick}
                    aria-controls="popup_menu_close_case"
                    aria-haspopup
                  />
                  <Menu
                    model={closeCaseMenuItems}
                    popup
                    ref={closeCaseMenu}
                    id="popup_menu_close_case"
                    popupAlignment="right"
                    // 4px corner radius + soft floating shadow +
                    // translate-y for a 4px gap below the trigger.
                    // The gray-110 border + gray-50 hover wash come
                    // from the shared Menu theme, which now matches
                    // the themed Dropdown panel in the sidebars.
                    pt={{
                      root: {
                        className: `w-60 !rounded-[4px] overflow-hidden translate-y-[4px] ${FLOATING_PANEL_SHADOW}`,
                      },
                    }}
                  />
                </>
              ) : (
                <Button
                  disabled={isCloseLoading}
                  severity="danger"
                  label={isCloseLoading ? 'Closing...' : 'Close'}
                  className="!h-8 px-4 whitespace-nowrap"
                  onClick={() => handleCloseCaseClick(false)}
                />
              ))}
          </div>
        </>
      )}
    </div>
  );
};
